// DpiScaled.jsx — anchor + anti-collapse + scroll fallback
import { useEffect, useMemo, useState, createContext, useContext } from "react";

// Create context to share the current DPI scale value
export const DpiScaleContext = createContext(1);

// Hook to access the current DPI scale
export const useDpiScale = () => {
  const scale = useContext(DpiScaleContext);
  return scale;
};

export default function DpiScaled({ children, baselineDpr = 1 }) {
  const getScale = () => {
    const dpr = window.devicePixelRatio || 1;
    const s = baselineDpr / dpr;
    return Math.max(0.25, Math.min(4, s)); // clamp for odd DPR/zoom reports
  };

  const [scale, setScale] = useState(getScale);

  useEffect(() => {
    const update = () => setScale(getScale());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    update();
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, [baselineDpr]);

  // Percentage compensation (no pixel rounding), absolutely anchored,
  // new formatting context (flow-root) to prevent first-child margin collapse,
  // and *overflow: auto* so you can always scroll when content is taller.
  const innerStyle = useMemo(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      width: `calc(100% / ${scale})`,
      height: `calc(100% / ${scale})`,
      transform: `translate3d(0,0,0) scale(${scale})`,
      transformOrigin: "0 0",
      display: "flow-root",
      overflow: "auto",
      // smooth / stable scrolling:
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "auto",
      scrollbarGutter: "stable both-edges",
      willChange: "transform",
      contain: "paint",
    }),
    [scale]
  );

  return (
    <DpiScaleContext.Provider value={scale}>
      <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
        <div style={innerStyle}>{children}</div>
      </div>
    </DpiScaleContext.Provider>
  );
}
